import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import './processingSummaryTable.scss';

/**
 * ProcessingSummaryTable Component
 * 
 * A table component built with `@tanstack/react-table` for displaying processing summary data.
 * It supports dynamic data and column configurations and provides row-click functionality.
 * 
 * @component
 * @memberof Components
 * @param {Object} props - Component props
 * @param {Array} props.data - The data to be displayed in the table.
 * @param {Array} props.columns - The column configuration for the table.
 * @param {Function} props.openDetailsPopUp - Function to be called when a row is clicked, receiving the `stepName` of the clicked row.
 * @param {string} [props.type] - Optional prop used as a dependency for memoization.
 * 
 * @example
 * const data = [
 *   { stepName: "Step 1", status: "Completed", date: "2024-03-14" },
 *   { stepName: "Step 2", status: "Pending", date: "2024-03-15" },
 * ];
 * 
 * const columns = [
 *   { accessorKey: "stepName", header: "Step Name" },
 *   { accessorKey: "status", header: "Status" },
 *   { accessorKey: "date", header: "Date" },
 * ];
 * 
 * <ProcessingSummaryTable data={data} columns={columns} openDetailsPopUp={(stepName) => console.log(stepName)} />
 * 
 * @returns {JSX.Element} The rendered table component.
 */

const ProcessingSummaryTable = (props) => {

    const data = useMemo(() => {
        return props.data;
    }, [props.type]);

    const columns = useMemo(() => {
        return props.columns;
    }, [props.type]);


    const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

    return (
        <div className='processing-summary-table-container' >
            <table className='table'>
                <thead className='thead'>
                    {table.getHeaderGroups().map((headerGroup, index) => (
                        <tr key={index}>
                            {headerGroup.headers.map((header, index) => {
                                const isLeftAligned = index === 0;
                                const headerStyle = {
                                    fontSize: '13px',
                                    fontWeight: 'normal',
                                    paddingLeft: isLeftAligned ? '10px' : '0px',
                                    textAlign: isLeftAligned ? 'left' : 'center', // Conditionally set the textAlign
                                    backgroundColor: 'white',
                                    color: 'black',
                                };
                                return (
                                    <th key={index} style={headerStyle}>
                                        <p style={{ padding: '10px 0px' }}>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </p>
                                    </th>
                                )
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody className='tbody'>
                    {table.getCoreRowModel().rows.map((row, index) => {
                        const rowStyle = {
                            borderBottom: '1px solid #DFDFDF',
                            borderTop: '1px solid #DFDFDF',
                            fontSize: '13px',
                            textAlign: 'center',
                            fontWeight: 'normal', // Conditionally set the fontWeight
                            backgroundColor: index % 2 === 0 ? '#d5e5ff' : 'white',

                        };

                        return (
                            <tr key={index} style={rowStyle} onClick={() => { props.openDetailsPopUp(row.original.stepName, row.original) }}>
                                {row.getVisibleCells().map((cell, index) => {
                                    const isLeftAligned = index === 0;
                                    const isDateAndTime = index === 2;
                                    const cellStyle =
                                    {
                                        borderBottom: '1px solid #DFDFDF', borderTop: '1px solid #DFDFDF', fontSize: '0.75vw',
                                        textAlign: isLeftAligned ? 'left' : 'center', // Conditionally set the textAlign
                                        paddingLeft: isLeftAligned ? '10px' : '0px',
                                        width: isDateAndTime ? '50px' : '100px'
                                    }
                                    return (
                                        <td key={index} style={cellStyle}>
                                            <p style={{ paddingTop: '0px', paddingBottom: '0px', minHeight: '40px' }}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </p>
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table >
        </div >
    );

}

export default ProcessingSummaryTable
